import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertCircle,
  Loader2,
  Send,
  Trash2,
  Undo2
} from 'lucide-react'

interface ConfirmationDialogsProps {
  showRejectDialog: number | null
  setShowRejectDialog: (id: number | null) => void
  showDeleteDialog: number | null
  setShowDeleteDialog: (id: number | null) => void
  showWithdrawDialog: number | null
  setShowWithdrawDialog: (id: number | null) => void
  rejectionReason: Record<number, string>
  setRejectionReason: (fn: (prev: Record<number, string>) => Record<number, string>) => void
  processingObjective: number | null
  deletingObjective: number | null
  withdrawingObjective: number | null
  onRejectObjective: (objectiveId: number) => void
  onDeleteObjective: (objectiveId: number) => void
  onWithdrawObjective: (objectiveId: number) => void
}

export default function ConfirmationDialogs({
  showRejectDialog,
  setShowRejectDialog,
  showDeleteDialog,
  setShowDeleteDialog,
  showWithdrawDialog,
  setShowWithdrawDialog,
  rejectionReason,
  setRejectionReason,
  processingObjective,
  deletingObjective,
  withdrawingObjective,
  onRejectObjective,
  onDeleteObjective,
  onWithdrawObjective
}: ConfirmationDialogsProps) {
  return (
    <>
      {/* Reject Confirmation Dialog */}
      {showRejectDialog && (
        <Dialog open={!!showRejectDialog} onOpenChange={(open) => !open && setShowRejectDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Objective</DialogTitle>
              <DialogDescription>
                Provide a clear reason for rejecting this objective. This will help the team member understand what needs to be improved.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Rejection Reason *</Label>
                <Textarea
                  placeholder="Explain why this objective is being rejected and what improvements are needed..."
                  value={rejectionReason[showRejectDialog] || ''}
                  onChange={(e) => setRejectionReason(prev => ({
                    ...prev,
                    [showRejectDialog]: e.target.value
                  }))}
                  className="min-h-[120px] mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum 10 characters required
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    The team member will be notified and can revise and resubmit this objective.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectDialog(null)}
                disabled={processingObjective === showRejectDialog}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => onRejectObjective(showRejectDialog)}
                disabled={
                  processingObjective === showRejectDialog ||
                  !rejectionReason[showRejectDialog] ||
                  rejectionReason[showRejectDialog].trim().length < 10
                }
              >
                {processingObjective === showRejectDialog ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Rejection
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={!!showDeleteDialog} onOpenChange={(open) => !open && setShowDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Objective</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this objective? This action will move it to the History tab with "deleted" as the reason.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-semibold">This objective will be:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Moved to the History (Rejected) tab</li>
                      <li>Marked with rejection reason: "Objective deleted by user"</li>
                      <li>No longer appear in Active objectives</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(null)}
                disabled={deletingObjective === showDeleteDialog}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => onDeleteObjective(showDeleteDialog)}
                disabled={deletingObjective === showDeleteDialog}
              >
                {deletingObjective === showDeleteDialog ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Objective
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Withdraw Confirmation Dialog */}
      {showWithdrawDialog && (
        <Dialog open={!!showWithdrawDialog} onOpenChange={(open) => !open && setShowWithdrawDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Withdraw Objective</DialogTitle>
              <DialogDescription>
                Are you sure you want to withdraw this pending objective?
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <Undo2 className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-semibold">This objective will be:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Removed from the Pending tab</li>
                      <li>Marked as withdrawn (kept for audit trail)</li>
                      <li>No longer visible in Active or Rejected tabs</li>
                    </ul>
                    <p className="mt-2 text-xs text-orange-600">
                      Withdrawal is only available before a collaborative session has been scheduled.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowWithdrawDialog(null)}
                disabled={withdrawingObjective === showWithdrawDialog}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                className="text-orange-700 border-orange-300 hover:bg-orange-50"
                onClick={() => onWithdrawObjective(showWithdrawDialog)}
                disabled={withdrawingObjective === showWithdrawDialog}
              >
                {withdrawingObjective === showWithdrawDialog ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Withdrawing...
                  </>
                ) : (
                  <>
                    <Undo2 className="h-4 w-4 mr-2" />
                    Withdraw Objective
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
